/**
 * Stem / kind field aliases for a twin-test follow-up (why).
 *
 * LEGACY PARSE ONLY. Twin-test no longer arms or edits paired OE on the wire
 * (Wave 1). `gateArtifacts.readQuestion` still recognizes leftover stems so an
 * old approved body does not crash the gate card; the UI does not surface or
 * re-emit them. Spend, unit counting, and add/remove-follow-up paths are gone.
 *
 * Mirrors backend `TWIN_TEST_FOLLOW_UP_STEM_KEYS` /
 * `TWIN_TEST_FOLLOW_UP_KIND_KEYS` in `twin-test-spec.mapper.ts` for the names
 * that may still appear on historical artifacts.
 */

/** Writer-declared stem keys (canonical first, then snake_case / legacy). */
export const GATE_FOLLOW_UP_STEM_KEYS: ReadonlyArray<string> = [
  'followUpStem',
  'follow_up_stem',
  'pairedOeStem',
  'paired_oe_stem',
]

/** Pairing kind keys (camelCase + snake_case). */
export const GATE_FOLLOW_UP_KIND_KEYS: ReadonlyArray<string> = [
  'pairedOe',
  'paired_oe',
]

/** Every field that must be cleared when the why is dropped. */
export const GATE_FOLLOW_UP_CLEAR_KEYS: ReadonlyArray<string> = [
  ...GATE_FOLLOW_UP_STEM_KEYS,
  ...GATE_FOLLOW_UP_KIND_KEYS,
]

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** First non-empty stem across the alias list, or null. */
export function readFollowUpStem(entry: Record<string, unknown>): string | null {
  for (const key of GATE_FOLLOW_UP_STEM_KEYS) {
    const stem = readNonEmptyString(entry[key])
    if (stem) {
      return stem
    }
  }
  return null
}

/** First non-empty pairing kind across the alias list, or null. */
export function readFollowUpKind(entry: Record<string, unknown>): string | null {
  for (const key of GATE_FOLLOW_UP_KIND_KEYS) {
    const kind = readNonEmptyString(entry[key])
    if (kind) {
      return kind
    }
  }
  return null
}

/**
 * Whether this raw question entry carries a follow-up (stem or pairing kind).
 * Latency / unit aside only — never a billed parent.
 */
export function entryHasFollowUp(entry: Record<string, unknown>): boolean {
  return readFollowUpStem(entry) !== null || readFollowUpKind(entry) !== null
}

/** Drop every stem/kind alias so a later read cannot re-arm the why. */
export function clearFollowUpFields(entry: Record<string, unknown>): void {
  for (const key of GATE_FOLLOW_UP_CLEAR_KEYS) {
    delete entry[key]
  }
}
